import React, { useCallback, useEffect, useRef, useState } from 'react';
import { materialService, batchService } from '../services/api';
import { useApp } from '../context/AppContext';
import { schoolToday } from '../utils/schoolTime';

const TeacherMaterials = () => {
  const { notify, currentUser } = useApp();
  const isAdmin = currentUser.role === 'admin';
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [materials, setMaterials] = useState([]);
  const [date, setDate] = useState(schoolToday());
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [commonMaterials, setCommonMaterials] = useState([]);
  const [subject, setSubject] = useState('');
  const [commonTitle, setCommonTitle] = useState('');
  const [commonUploading, setCommonUploading] = useState(false);
  const commonFileInputRef = useRef(null);

  // CEFR levels are always offered for a common material, even before any
  // batch at that level exists yet — a course book for B1 is useful to
  // upload ahead of time, not just once a B1 batch has been created.
  // C1/C2 are left off this pre-offered list for now (no classes at those
  // levels yet) — flip PRE_OFFERED_LEVELS back to the full scale once
  // that changes. Either way, any subject already in use by a batch
  // (including C1/C2, or a non-CEFR label) is added too, so nothing that
  // could previously be picked disappears.
  //
  // Admin-only: an admin manages the whole curriculum, so pre-offering
  // every level (even ones with no batch yet) makes sense for them. A
  // teacher should only ever see subjects they actually teach — `batches`
  // is already scoped to just their own (see batchService.list()), so for
  // a teacher the subject list is exactly that, nothing pre-offered.
  const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const PRE_OFFERED_LEVELS = ['A1', 'A2', 'B1', 'B2'];
  const subjects = [...new Set([...(isAdmin ? PRE_OFFERED_LEVELS : []), ...batches.map((b) => b.subject).filter(Boolean)])]
    .sort((a, b) => {
      const ai = CEFR_LEVELS.indexOf(a);
      const bi = CEFR_LEVELS.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });

  useEffect(() => {
    batchService.list().then(setBatches).catch((err) => notify(err.message, 'error'));
  }, [notify]);

  const loadMaterials = useCallback(() => {
    if (!selectedBatchId) return;
    materialService.listForBatch(selectedBatchId).then(setMaterials).catch((err) => notify(err.message, 'error'));
  }, [selectedBatchId, notify]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  const loadCommonMaterials = useCallback(() => {
    materialService.listCommon().then(setCommonMaterials).catch((err) => notify(err.message, 'error'));
  }, [notify]);

  useEffect(() => {
    loadCommonMaterials();
  }, [loadCommonMaterials]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current.files[0];
    if (!file) return notify('Choose a PDF file first', 'error');

    setUploading(true);
    try {
      await materialService.upload(selectedBatchId, date, title, file);
      notify('Material uploaded', 'success');
      setTitle('');
      fileInputRef.current.value = '';
      loadMaterials();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleUploadCommon = async (e) => {
    e.preventDefault();
    const file = commonFileInputRef.current.files[0];
    if (!subject) return notify('Choose a subject first', 'error');
    if (!file) return notify('Choose a PDF file first', 'error');

    setCommonUploading(true);
    try {
      await materialService.uploadCommon(subject, commonTitle, file);
      notify('Common material uploaded', 'success');
      setCommonTitle('');
      commonFileInputRef.current.value = '';
      loadCommonMaterials();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setCommonUploading(false);
    }
  };

  const handleDelete = async (m, reload) => {
    const confirmed = window.confirm(`Delete "${m.title}"?`);
    if (!confirmed) return;
    try {
      await materialService.remove(m.id);
      notify('Material deleted', 'success');
      reload();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleView = async (m) => {
    try {
      await materialService.view(m.id);
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleDownload = async (m) => {
    try {
      await materialService.download(m.id, m.originalName);
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  return (
    <div className="page">
      <h1>Class Materials</h1>
      <div className="card">
        <p className="stat-note">
          Students can see materials for today and earlier, plus the material for their next upcoming class.
        </p>
        <div className="inline-form">
          <select className="batch-select" value={selectedBatchId} onChange={(e) => setSelectedBatchId(e.target.value)}>
            <option value="">Select a batch…</option>
            {batches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>

        {selectedBatchId && (
          <>
            <form className="inline-form" onSubmit={handleUpload}>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
              <input
                placeholder="Title (optional)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <input type="file" accept="application/pdf" ref={fileInputRef} required />
              <button type="submit" className="btn-primary" disabled={uploading}>
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </form>

            <ul className="list">
              {materials.map((m) => (
                <li key={m.id}>
                  <span>{m.date} — {m.title}</span>
                  <div className="btn-group">
                    <button className="btn-link" onClick={() => handleView(m)}>View</button>
                    <button className="btn-link" onClick={() => handleDownload(m)}>Download</button>
                    {(isAdmin || m.uploadedBy === currentUser.id) && (
                      <button className="btn-link btn-danger" onClick={() => handleDelete(m, loadMaterials)}>Delete</button>
                    )}
                  </div>
                </li>
              ))}
              {materials.length === 0 && <li>No materials uploaded for this batch yet.</li>}
            </ul>
          </>
        )}
      </div>

      <div className="card">
        <h2>Common Materials</h2>
        <p className="stat-note">
          Shared by subject (e.g. a course book for A1) — visible to every student in any batch of that subject,
          with no date restriction.
        </p>
        <form className="inline-form" onSubmit={handleUploadCommon}>
          <select value={subject} onChange={(e) => setSubject(e.target.value)} required>
            <option value="">Select a subject…</option>
            {subjects.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <input
            placeholder="Title (optional)"
            value={commonTitle}
            onChange={(e) => setCommonTitle(e.target.value)}
          />
          <input type="file" accept="application/pdf" ref={commonFileInputRef} required />
          <button type="submit" className="btn-primary" disabled={commonUploading}>
            {commonUploading ? 'Uploading…' : 'Upload'}
          </button>
        </form>

        <ul className="list">
          {commonMaterials.map((m) => (
            <li key={m.id}>
              <span>{m.subject} — {m.title}</span>
              <div className="btn-group">
                <button className="btn-link" onClick={() => handleView(m)}>View</button>
                <button className="btn-link" onClick={() => handleDownload(m)}>Download</button>
                {(isAdmin || m.uploadedBy === currentUser.id) && (
                  <button className="btn-link btn-danger" onClick={() => handleDelete(m, loadCommonMaterials)}>Delete</button>
                )}
              </div>
            </li>
          ))}
          {commonMaterials.length === 0 && <li>No common materials uploaded yet.</li>}
        </ul>
      </div>
    </div>
  );
};

// Same short "Jul 19" style used across the app's other date displays
// (see src/pages/Attendance.js's formatShortDate) — plain local-time
// Date math is fine since this is just a display string, not a
// same-day comparison.
const formatMaterialDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const StudentMaterials = () => {
  const { notify } = useApp();
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    materialService.mine().then(setMaterials).catch((err) => notify(err.message, 'error'));
  }, [notify]);

  const today = schoolToday();

  const handleView = async (m) => {
    try {
      await materialService.view(m.id);
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  return (
    <div className="page">
      <h1>Class Materials</h1>
      {materials.length === 0 ? (
        <div className="card">
          <p>No materials available yet.</p>
        </div>
      ) : (
        <div className="material-grid">
          {materials.map((m) => (
            <div key={m.id} className="material-card">
              <div className="material-card-icon" aria-hidden="true">PDF</div>
              <div className="material-card-body">
                <h3 className="material-card-title">{m.title}</h3>
                <p className="material-card-meta">
                  {m.date ? `${m.batch.name} · ${formatMaterialDate(m.date)}` : m.subject}
                </p>
                {(m.date ? m.date > today : true) && (
                  <span className={`badge ${m.date ? 'badge-ended' : ''}`}>
                    {m.date ? 'Upcoming' : 'Course Material'}
                  </span>
                )}
              </div>
              <button className="btn-primary material-card-view" onClick={() => handleView(m)}>View</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Materials = () => {
  const { currentUser } = useApp();
  return currentUser.role === 'student' ? <StudentMaterials /> : <TeacherMaterials />;
};

export default Materials;
